// The periods the dropdown picks besides its presets — a week (Monday to
// Sunday), a month, a quarter, a year — and the step from one range to the
// next or the one before: a period by its own unit (Q4 → Q3), any other range
// by as many days as it has (8 days → the 8 before them).
import moment from "moment"

export const PERIOD_MODES = ["week", "month", "quarter", "year"]
export const isPeriodMode = (mode) => PERIOD_MODES.includes(mode)

// moment's unit for each mode: weeks begin on Monday whatever the locale
const UNIT = { week: "isoWeek", month: "month", quarter: "quarter", year: "year" }
const STEP = { week: "week", month: "month", quarter: "quarter", year: "year" }

/** The whole period of `mode` that `date` falls in: `{ startDate, endDate }`. */
export function periodOf(mode, date) {
  const unit = UNIT[mode]
  return { startDate: moment(date).startOf(unit).toDate(), endDate: moment(date).endOf(unit).toDate() }
}

/** The days a preset of `days` covers, today the last of them (as the reports read it). */
export function presetRange(days) {
  return { startDate: moment().subtract(days - 1, "days").startOf("day").toDate(), endDate: moment().endOf("day").toDate() }
}

/**
 * The range one step before (`dir` -1) or after (+1) `range`: a period of
 * `mode` moves by its unit, anything else by its own length in days.
 */
export function shiftRange(range, mode, dir) {
  if (!range?.startDate || !range?.endDate) return null
  if (isPeriodMode(mode)) return periodOf(mode, moment(range.startDate).add(dir, STEP[mode]))
  const start = moment(range.startDate).startOf("day")
  const end = moment(range.endDate).startOf("day")
  const days = end.diff(start, "days") + 1
  return {
    startDate: start.clone().add(dir * days, "days").toDate(),
    endDate: end.clone().add(dir * days, "days").endOf("day").toDate(),
  }
}

/** A range that has not begun yet is not offered: the reports have nothing in it. */
export const startsInFuture = (range) => !!range && moment(range.startDate).isAfter(moment().endOf("day"))

/** "Sep 18 - 24, 2026", "Sep 28 - Oct 4, 2026", "Dec 28, 2025 - Jan 3, 2026", "Sep 18, 2026". */
export function formatSpan(range, locale) {
  const from = moment(range.startDate)
  const to = moment(range.endDate)
  if (locale) {
    from.locale(locale)
    to.locale(locale)
  }
  if (from.isSame(to, "day")) return from.format("MMM D, YYYY")
  if (from.isSame(to, "month")) return `${from.format("MMM D")} - ${to.format("D, YYYY")}`
  if (from.isSame(to, "year")) return `${from.format("MMM D")} - ${to.format("MMM D, YYYY")}`
  return `${from.format("MMM D, YYYY")} - ${to.format("MMM D, YYYY")}`
}

/** What the button says for a range picked as `mode`. */
export function formatPeriod(range, mode, locale) {
  const from = moment(range.startDate)
  if (locale) from.locale(locale)
  if (mode === "month") return from.format("MMM YYYY")
  if (mode === "quarter") return `Q${from.quarter()} ${from.format("YYYY")}`
  if (mode === "year") return from.format("YYYY")
  return formatSpan(range, locale)
}
